import {
  hasOrientedSubmission,
  isAdditionalOrientingMark,
  isEnFace,
  isExcision,
  isExcisionWithTips,
  isShaveFragments,
  isOrientedEllipse,
  isHalves,
  isPunch,
  isMultipleExcisions,
  isMultiplePunches,
  isMultiplePunchesInSingleCassette,
  isMultipleShaves,
  isMultipleShavesInSingleCassette,
  isLipoma,
  GrossingCondition,
  isEyeWedge,
  isDIFSelected,
} from "./conditions";
import {
  falseMarginsSegments,
  foreignBodySegments,
  smallTissueSegments,
  softenedAndDecalcifiedSegments,
} from "./segmentsCommon";
import { GrossingSelections } from "./selections";

// GrossingSegments define the body of a Grossing Mad-Lib.
//
// content     - A string which will be output as part of the Mad-Lib.
//               May contain GrossingTokens with the syntax "{token}" and/or GrossingConditionals with the syntax "[conditional]".
// condition?  - A GrossingConditional which must return true for this GrossingSegment to be output.

export type GrossingSegment = {
  content: string;
  condition?: GrossingCondition;
};

export type RepeatingGrossingSegments = {
  segments: GrossingSegment[];
  countFn: (s: GrossingSelections) => number;
  condition?: GrossingCondition;
};

export const grossingSegments: Array<
  GrossingSegment | RepeatingGrossingSegments
> = [
  {
    content:
      'The specimen received in {medium} labeled with patient name and "{source}"',
    condition: (s) => !isDIFSelected(s),
  },
  {
    content:
      "The specimen received in {medium} is a {measurement_d} cm diameter and a {measurement_l} cm length {type} of skin. The specimen is entirely submitted in block {cassette_count} for direct immunofluorescent study",
    condition: (s) => isDIFSelected(s),
  },
  {
    content: " is [typeAn] [typeSpace]{type} of [specimenType]",
    condition: (s) =>
      !isMultipleShaves(s) &&
      !isMultipleExcisions(s) &&
      !isMultiplePunches(s) &&
      !isShaveFragments(s) &&
      !isEyeWedge(s) &&
      !isDIFSelected(s),
  },
  {
    content: " is multiple {type} fragments",
    condition: isShaveFragments,
  },
  {
    content:
      " are {type_count} {type} of [specimenType] submitted in cassettes {cassette_count}",
    condition: (s) =>
      isMultipleExcisions(s) ||
      (isMultiplePunches(s) && !isMultiplePunchesInSingleCassette(s)) ||
      (isMultipleShaves(s) && !isMultipleShavesInSingleCassette(s)),
  },
  {
    content: " are {type_count} {type} of [specimenType]",
    condition: (s) =>
      (isMultipleShaves(s) && isMultipleShavesInSingleCassette(s)) ||
      (isMultiplePunches(s) && isMultiplePunchesInSingleCassette(s)),
  },
  {
    content: " is a wedge of eyelid margin with skin and conjunctiva",
    condition: isEyeWedge,
  },
  {
    content:
      " measuring {measurement_w} cm x {measurement_l} cm x {measurement_h} cm[with][subcutaneous]",
    condition: (s) =>
      !isMultipleExcisions(s) &&
      !isMultiplePunches(s) &&
      !isMultipleShaves(s) &&
      !isDIFSelected(s),
  },
  {
    content: " {withA}",
    condition: (s) =>
      !isMultipleExcisions(s) &&
      !isMultipleShaves(s) &&
      !isMultiplePunches(s) &&
      s.modifierSelections.withA,
  },
  {
    content: " {lipoma_medium}",
    condition: isLipoma,
  },
  {
    content: ".",
  },
  {
    content: " No necrosis or hemorrhage is noted.",
    condition: isLipoma,
  },
  {
    content: " Specimen is processed en face.",
    condition: isEnFace,
  },
  {
    content: " Sectioning reveals a cyst-like structure.",
    condition: (s) =>
      (isPunch(s) || isExcision(s)) &&
      s.modifierSelections.cyst &&
      !s.modifierSelections.measurement,
  },
  {
    content:
      " Sectioning reveals a cyst-like structure measuring {cyst_measurement} cm in greatest dimension.",
    condition: (s) =>
      (isPunch(s) || isExcision(s)) &&
      s.modifierSelections.cyst &&
      s.modifierSelections.measurement,
  },
  {
    content:
      " There is an orienting {orienting_mark} at the {orientation_margin} margin. Inked as follows: " +
      "{orientation_blue_a} to {orientation_blue_b} inked {orientation_color_blue}; " +
      "{orientation_yellow_a} to {orientation_yellow_b} inked {orientation_color_yellow}. ",
    condition: (s) =>
      isOrientedEllipse(s) && isHalves(s) && !isAdditionalOrientingMark(s),
  },
  {
    content:
      " There is an orienting {orienting_mark} at the {orientation_margin} margin and an orienting {additional_orienting_mark} at the {additional_orientation_margin} margin. Inked as follows: " +
      "{orientation_blue_a} to {orientation_blue_b} inked {orientation_color_blue}; " +
      "{orientation_yellow_a} to {orientation_yellow_b} inked {orientation_color_yellow}. ",
    condition: (s) =>
      isOrientedEllipse(s) && isHalves(s) && isAdditionalOrientingMark(s),
  },
  {
    content:
      " There is an orienting {orienting_mark} at the {orientation_margin} margin. Inked as follows: " +
      "{orientation_blue_a} to {orientation_blue_b} inked {orientation_color_blue}; " +
      "{orientation_green_a} to {orientation_green_b} inked {orientation_color_green}; " +
      "{orientation_black_a} to {orientation_black_b} inked {orientation_color_black}; ",
    condition: (s) =>
      isOrientedEllipse(s) && !isHalves(s) && !isAdditionalOrientingMark(s),
  },
  {
    content:
      " There is an orienting {orienting_mark} at the {orientation_margin} margin and an orienting {additional_orienting_mark} at the {additional_orientation_margin} margin. Inked as follows: " +
      "{orientation_blue_a} to {orientation_blue_b} inked {orientation_color_blue}; " +
      "{orientation_green_a} to {orientation_green_b} inked {orientation_color_green}; " +
      "{orientation_black_a} to {orientation_black_b} inked {orientation_color_black}; ",
    condition: (s) =>
      isOrientedEllipse(s) && !isHalves(s) && isAdditionalOrientingMark(s),
  },
  {
    content:
      " {orientation_red_a} to {orientation_red_b} inked {orientation_color_red}.",
    condition: (s) => isOrientedEllipse(s) && !isEnFace(s) && !isHalves(s),
  },
  {
    content:
      " {orientation_yellow_a} to {orientation_yellow_b} inked {orientation_color_yellow}. " +
      "Specimen is serially sectioned as follows: starting with the peripheral sections " +
      "#1 from {sectioned_a} to {sectioned_b}; #2 from {sectioned_a} to {sectioned_b}; #3 from {sectioned_a} to {sectioned_b}; " +
      "#4 from {sectioned_a} to {sectioned_b}; #5 from {sectioned_a} to {sectioned_b}; #6 from {sectioned_a} to {sectioned_b}. " +
      "The remaining tissue is serially sectioned ",
    condition: (s) => isEnFace(s) && !isHalves(s),
  },
  {
    content:
      " Specimen is {ink} {base_ink_color}[grossedComma]{grossed}[grossedComma]and {submission} submitted ",
    condition: (s, settings) =>
      !isShaveFragments(s) &&
      !isMultipleExcisions(s) &&
      !isMultipleShaves(s) &&
      !isMultiplePunches(s) &&
      !isEnFace(s) &&
      !isOrientedEllipse(s) &&
      !isEyeWedge(s) &&
      !isDIFSelected(s) &&
      s.tokenSelections.ink?.value === "inked" &&
      !!settings?.base_ink_color?.active,
  },
  // NOTE: The above and below are the same "segment", but Propath wants to rotate inking colours
  // ... until such a time they are settled into doing everything "green" for the AI
  {
    content:
      " Specimen is {ink}[grossedComma]{grossed}[grossedComma]and {submission} submitted ",
    condition: (s, settings) =>
      !isShaveFragments(s) &&
      !isMultipleExcisions(s) &&
      !isMultipleShaves(s) &&
      !isMultiplePunches(s) &&
      !isEnFace(s) &&
      !isOrientedEllipse(s) &&
      !isEyeWedge(s) &&
      !isDIFSelected(s) &&
      !(
        s.tokenSelections.ink?.value === "inked" &&
        !!settings?.base_ink_color?.active
      ),
  },
  {
    content: " Specimen is {grossed}[grossedAnd] {submission} submitted ",
    condition: (s) =>
      !isShaveFragments(s) &&
      !isMultipleExcisions(s) &&
      !isMultipleShaves(s) &&
      !isMultiplePunches(s) &&
      !isEnFace(s) &&
      isOrientedEllipse(s) &&
      !isEyeWedge(s),
  },
  {
    content: "for histology",
    condition: (s) =>
      !isShaveFragments(s) &&
      !isMultipleExcisions(s) &&
      !isMultipleShaves(s) &&
      !isMultiplePunches(s) &&
      !isEnFace(s) &&
      !hasOrientedSubmission(s) &&
      !isDIFSelected(s) &&
      !isEyeWedge(s),
  },
  {
    content: "from {orientation_submission_a} to {orientation_submission_b}",
    condition: (s) =>
      !isShaveFragments(s) &&
      !isMultipleExcisions(s) &&
      !isMultipleShaves(s) &&
      !isMultiplePunches(s) &&
      (hasOrientedSubmission(s) || isEnFace(s)),
  },
  {
    content: " as {cassette_count}.",
    condition: (s) =>
      !isShaveFragments(s) &&
      !isMultipleExcisions(s) &&
      !isMultipleShaves(s) &&
      !isMultiplePunches(s) &&
      !isEyeWedge(s) &&
      !isDIFSelected(s),
  },
  {
    content:
      " The medial margin is inked {ew_medial_color} and the lateral margin is inked {ew_lateral_color}." +
      " The epidermis and conjunctiva are entirely separated and the false margin is inked {ew_false_color}." +
      " Each half is transversely sectioned. The specimen is submitted entirely in {cassette_count}." +
      " The epidermis is submitted in {ew_epidermis_cassette_choice} and the conjunctiva is submitted in {ew_conjunctiva_cassette_choice}.",
    condition: isEyeWedge,
  },
  {
    content:
      " The specimen fragments are {submission} submitted in cassettes {cassette_count}.",
    condition: isShaveFragments,
  },
  {
    content: " Tips are submitted in cassette [specimenName]1.",
    condition: isExcisionWithTips,
  },
  {
    segments: [
      {
        content:
          " The [iOrder] one, measuring {measurement_w} cm x {measurement_l} cm x {measurement_h} cm,",
      },
      {
        content: " with the skin surface containing {withA},",
        condition: (s) => s.modifierSelections.withA,
      },
      {
        content: " is inked {inked_color}[grossedComma]",
        condition: (s) =>
          !isMultipleShavesInSingleCassette(s) &&
          !isMultiplePunchesInSingleCassette(s),
      },
      {
        content:
          " {grossed}[grossedAnd] {submission} submitted for histology as {type_cassette_count}.",
        condition: (s) =>
          !isMultipleShavesInSingleCassette(s) &&
          !isMultiplePunchesInSingleCassette(s) &&
          s.tokenSelections.type_count?.value !==
            s.tokenSelections.cassette_count?.value,
      },
      {
        content:
          " {grossed}[grossedAnd] {submission} submitted for histology as [specimenName][i].",
        condition: (s) =>
          !isMultipleShavesInSingleCassette(s) &&
          !isMultiplePunchesInSingleCassette(s) &&
          s.tokenSelections.type_count?.value ===
            s.tokenSelections.cassette_count?.value,
      },
      {
        content: " is inked {inked_color}[grossedAnd] {grossed}.",
        condition: (s) =>
          isMultipleShavesInSingleCassette(s) ||
          isMultiplePunchesInSingleCassette(s),
      },
    ],
    countFn: (s) => parseInt(s.tokenSelections.type_count?.value || "") || 2,
    condition: (s) =>
      isMultipleShaves(s) || isMultipleExcisions(s) || isMultiplePunches(s),
  },
  {
    content:
      " All shaves are {submission} submitted in cassette [specimenName].",
    condition: (s) =>
      isMultipleShavesInSingleCassette(s) ||
      isMultiplePunchesInSingleCassette(s),
  },
  ...smallTissueSegments,
  ...falseMarginsSegments,
  ...foreignBodySegments,
  ...softenedAndDecalcifiedSegments,
];

export default grossingSegments;
